import { FC, useState, useRef, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
} from "@material-ui/core";
import CustomSkeleton from "src/components/skeleton/Skeleton";
import FormikForm, {
  FormikStep,
} from "src/components/ui-components/FormikForm";
import ConfigUserGroupType from "./Types/ConfigUserGroup";
import * as Yup from "yup";
import InputField from "src/components/ui-components/InputField";
import SwitchField from "src/components/ui-components/SwitchField";
import {
  getConfigUserGroupByKey,
  insertUpdateConfigUserGroup,
} from "./ConnectionService";

interface ModalProps {
  open: boolean;
  handleClose: any;
  configUserGroupKey?: string | null;
}

const defaultForm: ConfigUserGroupType = {
  ConfigUserGroupKey: null,
  ConfigUserGroup: "",
  IsDisabled: false,
};

const UserGroupsCreateEditModal: FC<ModalProps> = ({
  open,
  handleClose,
  configUserGroupKey = null,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formRef = useRef(null);

  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, isEdit: configUserGroupKey && true },
      });
    }
  };

  const getConfigUserGroup = async () => {
    setIsFetching(true);
    const res = await getConfigUserGroupByKey(configUserGroupKey);
    const existingValues = {
      ConfigUserGroupKey: configUserGroupKey,
      ConfigUserGroup: res.ConfigUserGroup,
      IsDisabled: res.IsDisabled,
    };
    resetFormData(existingValues);
    setIsFetching(false);
  };

  useEffect(() => {
    if (configUserGroupKey) {
      getConfigUserGroup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configUserGroupKey]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="Config User Group Create Edit"
        aria-describedby="Create Edit Form for Config User Group"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            width: "70%",
          }}
        >
          <Typography
            sx={{ marginBottom: "2%", fontWeight: "bold" }}
            variant="h5"
          >
            {`${configUserGroupKey ? "Edit" : "Create"} User Group`}
          </Typography>
          {isFetching && <CustomSkeleton skeletonType="FormEdit" />}
          <Box sx={{ display: isFetching ? "none" : "" }}>
            <FormikForm
              innerRef={formRef}
              initialValues={defaultForm}
              onSubmit={async (values): Promise<void> => {
                setIsLoading(true);
                const result = await insertUpdateConfigUserGroup(values);
                if (result) {
                  setIsLoading(false);
                  handleClose();
                } else {
                  setIsLoading(false);
                }
              }}
            >
              <FormikStep
                validationSchema={Yup.object({
                  ConfigUserGroup: Yup.string()
                    .nullable()
                    .max(
                      100,
                      "User Group name cannot be more than 100 characters"
                    )
                    .required("User Group name is required")
                    .matches(
                      /^(?!^0+$)[a-zA-Z0-9-]+(?: [a-zA-Z0-9-]+)*$/,
                      "Please enter valid User Group name"
                    ),
                  IsDisabled: Yup.bool().nullable(),
                })}
              >
                <Grid spacing={2} container>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <InputField
                      label="Config User Group *"
                      name="ConfigUserGroup"
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <SwitchField
                      name="IsDisabled"
                      label="Is Disabled"
                      labelPlacement="end"
                      disabled={isLoading}
                    />
                  </Grid>
                </Grid>
                <Grid spacing={2} container justifyContent="end">
                  <Button
                    sx={{
                      minWidth: 100,
                      marginTop: "2%",
                      marginRight: "2%",
                      color: "rgb(107, 119, 140)",
                    }}
                    disabled={isLoading}
                    variant="outlined"
                    type="button"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    sx={{
                      background:
                        "linear-gradient(to bottom, #072f40, #072f40)",
                      minWidth: 100,
                      marginTop: "2%",
                    }}
                    disabled={isLoading}
                    variant="contained"
                  >
                    Save
                    {isLoading && (
                      <CircularProgress
                        color="inherit"
                        thickness={5}
                        size={20}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Button>
                </Grid>
              </FormikStep>
            </FormikForm>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UserGroupsCreateEditModal;
